import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  createHandleDeleteLine,
  createInsertEmptyLine,
  createSetLineEdit,
  handleDrop as handleDropUtil,
} from "services/utility/lineItems";
import { isPersistedRecord } from "services/utility/misc";

/**
 * Custom hook that provides all line item manipulation handlers.
 *
 * This hook encapsulates:
 * - readOnlyLineIds state management
 * - All handler functions for line item manipulation
 *
 * It creates handler functions for:
 * - Setting a line to edit mode (removes from readOnlyLineIds)
 * - Deleting a line (with proper index management)
 * - Inserting an empty line (with proper index management)
 * - Handling drag-and-drop reordering (with proper index updates)
 *
 * The readOnlyLineIds Set is only populated when:
 * - transactionEditMode === "rowByRow" (in user settings)
 * - AND the record is persisted (has an ID)
 *
 * @param {number|string} recordId - The ID of the record (transaction)
 * @param {Array} lines - Array of line items
 * @param {Function} lineHandler - The line handler function from the parent component
 * @param {Object} emptyLineItem - The empty line item template for this object type
 * @returns {Object} Object containing:
 *   - readOnlyLineIds: Set of line indices that are read-only (not editable)
 *   - setReadOnlyLineIds: State setter for readOnlyLineIds (used in drag-and-drop)
 *   - setLineEdit: Function to make a line editable
 *   - handleDeleteLine: Function to delete a line
 *   - insertEmptyLine: Function to insert an empty line
 *   - handleDrop: Function to handle drag-and-drop reordering
 */
export function useLineItemHandlers(
  recordId,
  lines,
  lineHandler,
  emptyLineItem
) {
  const transactionEditMode = useSelector(
    (state) => state.userCompanySettings.settings.transactionEditMode
  );

  // Manages which line items are in read-only mode (locked for editing)
  // Only applies when transactionEditMode is "rowByRow" and record is persisted
  const [readOnlyLineIds, setReadOnlyLineIds] = useState(() => {
    // Only apply read-only mode when transactionEditMode is "rowByRow"
    // and the record is persisted (not a new record)
    if (transactionEditMode === "rowByRow" && isPersistedRecord(recordId)) {
      return new Set(lines.map((_, index) => index));
    }
    return new Set();
  });

  // Create handler functions using factory functions
  const setLineEdit = useMemo(
    () => createSetLineEdit(setReadOnlyLineIds),
    [setReadOnlyLineIds]
  );

  const handleDeleteLine = useMemo(
    () => createHandleDeleteLine(lineHandler, setReadOnlyLineIds),
    [lineHandler, setReadOnlyLineIds]
  );

  const insertEmptyLine = useMemo(
    () => createInsertEmptyLine(lineHandler, setReadOnlyLineIds, emptyLineItem),
    [lineHandler, setReadOnlyLineIds, emptyLineItem]
  );

  // Wrapper for handleDrop that passes the required parameters
  const handleDrop = useMemo(
    () => (result) =>
      handleDropUtil(result, lineHandler, lines, setReadOnlyLineIds),
    [lineHandler, lines, setReadOnlyLineIds]
  );

  return {
    readOnlyLineIds,
    setReadOnlyLineIds,
    setLineEdit,
    handleDeleteLine,
    insertEmptyLine,
    handleDrop,
  };
}
